import { useRef, useState } from "react";
import http from "@/lib/api";
import { Upload, X, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Resolve a stored image URL: if it's a relative /api/uploads path, prefix the backend.
export function resolveImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("/api/uploads/")) return `${BACKEND_URL}${url}`;
  return url;
}

// Append Unsplash optimization params (width, quality, format, crop) for performance.
// Safe: only touches images.unsplash.com URLs; passes others through untouched.
export function optimizeImageUrl(url, { w = 800, q = 75 } = {}) {
  if (!url || typeof url !== "string") return url || "";
  // Pass-through for uploaded files
  if (url.startsWith("/api/uploads/")) return resolveImageUrl(url);
  // Unsplash supports rich URL params + automatic WebP/AVIF via auto=format
  if (/images\.unsplash\.com/.test(url)) {
    try {
      const u = new URL(url);
      u.searchParams.set("w", String(w));
      u.searchParams.set("q", String(q));
      u.searchParams.set("auto", "format");
      u.searchParams.set("fit", "crop");
      return u.toString();
    } catch {
      return url;
    }
  }
  // For all other remote images (emergent CDN PNGs, etc.), route through wsrv.nl
  // — free public image proxy on Cloudflare. Converts PNG/JPG → WebP, resizes, caches.
  // Docs: https://images.weserv.nl/docs
  if (/^https?:\/\//i.test(url)) {
    try {
      const target = url.replace(/^https?:\/\//, "");
      const params = new URLSearchParams({
        url: target,
        w: String(w),
        q: String(q),
        output: "webp",
        we: "1", // without enlargement
      });
      return `https://wsrv.nl/?${params.toString()}`;
    } catch {
      return url;
    }
  }
  return url;
}

// SmartImage: drop-in <img> with lazy loading, async decoding, automatic
// CDN proxy (Unsplash native params OR wsrv.nl WebP conversion for other sources),
// explicit width/height for CLS, and retina srcset.
export function SmartImage({ src, alt = "", width = 800, height, quality = 75, eager = false, className, ...rest }) {
  const resolved = resolveImageUrl(src);
  const primary = optimizeImageUrl(resolved, { w: width, q: quality });
  const retina = optimizeImageUrl(resolved, { w: Math.round(width * 2), q: quality });
  const isProxied = primary !== resolved;
  // Default aspect-ratio if no height provided (16:10 — closest to most card images)
  const h = height || Math.round(width * 0.625);
  return (
    <img
      src={primary}
      srcSet={isProxied ? `${primary} 1x, ${retina} 2x` : undefined}
      alt={alt}
      width={width}
      height={h}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      fetchPriority={eager ? "high" : "auto"}
      className={className}
      {...rest}
    />
  );
}

export default function ImageUploadField({ value, onChange, label = "Görsel", endpoint = "/uploads", helpText }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setErr(""); setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await http.post(endpoint, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (e) {
      setErr(e.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  return (
    <div className="md:col-span-2" data-testid="image-upload-field">
      <label className="label-tiny block mb-2">{label}</label>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
        {/* Preview */}
        <div className="md:col-span-3">
          {value ? (
            <div className="relative aspect-[4/3] bg-[hsl(var(--surface))] overflow-hidden border border-[hsl(var(--border))]">
              <img src={resolveImageUrl(value)} alt="" className="w-full h-full object-cover" data-testid="image-preview" loading="lazy" decoding="async" />
              <button type="button" className="absolute top-1 right-1 bg-white/90 p-1 hover:bg-[hsl(var(--destructive))] hover:text-white transition" onClick={() => onChange("")} title="Kaldır" data-testid="image-remove">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="aspect-[4/3] bg-[hsl(var(--surface))] border border-dashed border-[hsl(var(--border))] flex items-center justify-center text-[hsl(var(--muted-fg))] text-xs">
              Görsel yok
            </div>
          )}
        </div>

        {/* Upload zone + URL input */}
        <div className="md:col-span-9 space-y-2">
          <div
            onClick={() => inputRef.current?.click()}
            onDrop={onDrop}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            className="border-2 border-dashed border-[hsl(var(--border))] hover:border-[hsl(var(--terracotta))] p-5 text-center cursor-pointer transition"
            data-testid="image-dropzone"
          >
            {uploading ? (
              <div className="flex items-center justify-center gap-2 text-[hsl(var(--muted-fg))]">
                <Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor…
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1">
                <Upload className="w-5 h-5 text-[hsl(var(--terracotta))]" />
                <p className="text-sm">Bilgisayardan görsel yükle</p>
                <p className="text-xs text-[hsl(var(--muted-fg))]">{helpText || "JPG / PNG / WEBP — max 8MB · otomatik optimize edilir"}</p>
              </div>
            )}
            <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])} data-testid="image-file-input" />
          </div>

          <div className="text-xs text-[hsl(var(--muted-fg))] flex items-center gap-2">
            <span>veya link yapıştır:</span>
            <input
              className="input text-xs py-1 flex-1"
              placeholder="https://..."
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              data-testid="image-url-input"
            />
          </div>
          {err && <p className="text-xs text-[hsl(var(--destructive))]">{err}</p>}
        </div>
      </div>
    </div>
  );
}
